import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

export const CTA_URL =
  "https://greyrockai.app/?utm_source=seo&utm_medium=organic&utm_campaign=pattern_cluster";

export const ctaClass = cn(
  "inline-flex items-center justify-center gap-2 px-8 h-12 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black text-sm font-semibold tracking-wide transition-all",
  "shadow-[0_0_40px_-6px_rgba(34,211,238,0.85)] hover:shadow-[0_0_60px_-4px_rgba(34,211,238,1)] animate-cyanPulse"
);

export const CTAButton = ({
  label = "Analyze your own conversation →",
  href = CTA_URL,
}: {
  label?: string;
  href?: string;
}) => (
  <div className="mt-8 text-center">
    <a href={href} className={ctaClass}>
      {label}
    </a>
  </div>
);

export const Blockquote = ({ children }: { children: React.ReactNode }) => (
  <blockquote className="my-4 border-l-2 border-cyan-400/50 bg-white/[0.03] px-5 py-3 rounded-r-md text-white/80 italic font-serif text-[17px] leading-relaxed">
    {children}
  </blockquote>
);

export const SpeakerLine = ({
  speaker,
  line,
}: {
  speaker: string;
  line: string;
}) => (
  <div className="not-italic mb-2 last:mb-0">
    <span className="text-white/45 font-mono uppercase tracking-[0.18em] text-[12px] mr-2">
      {speaker}
    </span>
    <span className="text-white/80 italic font-serif text-[17px] leading-relaxed">
      "{line}"
    </span>
  </div>
);

export const P = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75 mt-4 first:mt-0">
    {children}
  </p>
);

export const H2 = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-5 mt-14">
    {children}
  </h2>
);

export const SeoPageShell = ({ children }: { children: React.ReactNode }) => (
  <div
    className="min-h-screen bg-black text-white"
    style={{
      background:
        "radial-gradient(ellipse at top, rgba(34,211,238,0.06) 0%, rgba(0,0,0,1) 60%)",
    }}
  >
    <header className="px-5 py-4 border-b border-white/[0.06]">
      <div className="max-w-[720px] mx-auto flex items-center justify-between gap-4">
        <Link
          to="/"
          className="text-[11px] font-mono uppercase tracking-[0.25em] text-cyan-300/80 hover:text-cyan-200 transition-colors"
        >
          ← GreyRock AI®
        </Link>
        <div className="flex items-center gap-4 md:gap-5">
          <Link
            to="/resources"
            className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/55 hover:text-cyan-200 transition-colors"
          >
            Resources
          </Link>
          <a
            href={CTA_URL}
            className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/55 hover:text-cyan-200 transition-colors"
          >
            Try the analyzer →
          </a>
        </div>
      </div>
    </header>

    <main className="px-5 py-12 md:py-20">
      <article className="max-w-[720px] mx-auto">{children}</article>
    </main>

    <footer className="border-t border-cyan-400/10 mt-16 py-6 px-5 text-center text-[11px] font-mono text-white/30 tracking-wider">
      <div>GREYROCKAI® · BEHAVIORAL INTELLIGENCE</div>
      <div className="mt-1.5">
        A{" "}
        <a href="https://shouldistudios.com" target="_blank" rel="noopener" className="hover:text-cyan-300/80 transition-colors">
          Should I Studios
        </a>{" "}
        product
      </div>
      <div className="mt-1.5 flex items-center justify-center gap-4">
        <Link to="/" className="hover:text-cyan-300/80 transition-colors">Home</Link>
        <span>·</span>
        <Link to="/privacy" className="hover:text-cyan-300/80 transition-colors">Privacy</Link>
        <span>·</span>
        <Link to="/professionals" className="hover:text-cyan-300/80 transition-colors">For Professionals</Link>
        <span>·</span>
        <Link to="/terms" className="hover:text-cyan-300/80 transition-colors">Terms</Link>
        <span>·</span>
        <Link to="/contact" className="hover:text-cyan-300/80 transition-colors">Contact</Link>
      </div>
    </footer>

    <style>{`
      @keyframes cyanPulseLocal {
        0%, 100% { box-shadow: 0 0 40px -6px rgba(34,211,238,0.7); }
        50% { box-shadow: 0 0 60px -2px rgba(34,211,238,1); }
      }
      .animate-cyanPulse { animation: cyanPulseLocal 3.2s ease-in-out infinite; }
    `}</style>
  </div>
);

export const HeroH1 = ({ children }: { children: React.ReactNode }) => (
  <h1 className="text-[2rem] md:text-[3rem] font-semibold tracking-tight leading-[1.1] mb-8">
    {children}
  </h1>
);

export const InlineLink = ({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) => (
  <Link
    to={to}
    className="text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
  >
    {children}
  </Link>
);
